import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck, TriangleAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (!token) return setState("invalid");
      try {
        const res = await fetch(`${API}/auth/reset-password?token=${encodeURIComponent(token)}`);
        setState(res.ok ? "valid" : "invalid");
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    setError("");
    if (data.password !== data.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: data.password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Reset failed");
      setState("done");
      toast.success("Password updated.", { description: "You can now sign in with your new password." });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="reset-password-page" className="relative mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-4 pb-24 pt-32 sm:px-8">
      {state === "checking" && (
        <div className="flex justify-center" data-testid="reset-checking"><Loader2 size={28} className="animate-spin text-[#1E3A8A]" /></div>
      )}

      {state === "invalid" && (
        <div className="card-dark p-8 text-center sm:p-10 hover:transform-none" data-testid="reset-invalid">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-red-500/40 bg-red-50">
            <TriangleAlert size={20} className="text-red-600" />
          </span>
          <h1 className="mt-6 font-sans text-2xl text-[#0F172A]">Link expired or invalid</h1>
          <p className="mt-3 text-sm leading-relaxed text-[#64748B]">
            This reset link is no longer valid. Request a fresh one from the sign-in page.
          </p>
          <Link to="/admin" data-testid="reset-back-login" className="btn-ghost mt-8 !px-6 !py-3 !text-xs">Back to Sign In</Link>
        </div>
      )}

      {state === "valid" && (
        <div className="card-dark p-8 sm:p-10 hover:transform-none" data-testid="reset-form-card">
          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#1E3A8A]/40 bg-[#1E3A8A]/10">
            <ShieldCheck size={20} className="text-[#1E3A8A]" />
          </span>
          <h1 className="mt-6 font-sans text-2xl text-[#0F172A] sm:text-3xl">Choose a new password</h1>
          <p className="mt-2 text-sm text-[#94A3B8]">At least 8 characters — longer and mixed is stronger.</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-7" data-testid="reset-password-form">
            <div>
              <label className="field-label" htmlFor="reset-password">New password <span className="text-[#1E3A8A]">*</span></label>
              <input id="reset-password" name="password" type="password" required minLength={8} data-testid="reset-password-input" className="field" placeholder="••••••••" autoComplete="new-password" />
            </div>
            <div>
              <label className="field-label" htmlFor="reset-confirm">Confirm new password <span className="text-[#1E3A8A]">*</span></label>
              <input id="reset-confirm" name="confirm" type="password" required minLength={8} data-testid="reset-confirm-input" className="field" placeholder="••••••••" autoComplete="new-password" />
            </div>
            {error && (
              <p data-testid="reset-error" className="rounded-lg border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>
            )}
            <button type="submit" disabled={submitting} data-testid="reset-submit-button" className="btn-gold w-full disabled:opacity-60 disabled:hover:translate-y-0">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : "Update Password"}
            </button>
          </form>
        </div>
      )}

      {state === "done" && (
        <div className="card-dark p-8 text-center sm:p-10 hover:transform-none" data-testid="reset-success">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#1E3A8A]/40 bg-[#1E3A8A]/10">
            <CheckCircle2 size={22} className="text-[#1E3A8A]" />
          </span>
          <h1 className="mt-6 font-sans text-2xl text-[#0F172A]">Password updated</h1>
          <p className="mt-3 text-sm leading-relaxed text-[#64748B]">Your admin password has been changed. Sign in with your new password.</p>
          <Link to="/admin" data-testid="reset-goto-login" className="btn-gold mt-8 !px-6 !py-3 !text-xs">Sign In</Link>
        </div>
      )}
    </section>
  );
}
